import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, ChevronRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { eventsAPI } from '../../services/api';
import { Event, EventType, eventGroupLabel, getEventTypeLabel } from '../../types/event';

const DAYS_AHEAD = 14;

const typeAccent: Record<EventType, string> = {
  [EventType.Training]: 'bg-blue-500',
  [EventType.Match]: 'bg-red-500',
  [EventType.Meeting]: 'bg-purple-500',
  [EventType.Other]: 'bg-gray-400',
};

const typeBadge: Record<EventType, string> = {
  [EventType.Training]: 'bg-blue-50 text-blue-700',
  [EventType.Match]: 'bg-red-50 text-red-700',
  [EventType.Meeting]: 'bg-purple-50 text-purple-700',
  [EventType.Other]: 'bg-gray-100 text-gray-700',
};

const time = (value: string) => new Date(value).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });

/** "Ma", "Holnap", különben "okt. 14., kedd". */
const dayLabel = (value: string) => {
  const date = new Date(value);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Ma';
  if (date.toDateString() === tomorrow.toDateString()) return 'Holnap';
  const text = date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', weekday: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * A következő két hét eseményei a főoldalon, napok szerint. Minden kártya
 * egy link: megnyitja az eseményt, ahol a részletek, a visszajelzés és az
 * utazás van. A kártyán csak a lényeg: mikor, hol, és hányan jönnek.
 */
const DashboardEvents: React.FC = () => {
  const [events, setEvents] = useState<Event[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    eventsAPI
      .getUpcoming(DAYS_AHEAD)
      .then((list) => {
        if (!cancelled) setEvents(list.filter((e) => new Date(e.endTime) > new Date()));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <CalendarIcon className="h-5 w-5 text-primary-600" />
          Közelgő események
        </h2>
        <Link to="/events" className="flex items-center gap-0.5 text-sm font-medium text-primary-600 hover:underline">
          Összes
          <ChevronRightIcon className="h-4 w-4" />
        </Link>
      </div>

      {events === null ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-white shadow-sm" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
          A következő két hétben nincs esemény.
        </div>
      ) : (
        <div className="space-y-4">
          {groupByDay(events).map(([day, dayEvents]) => (
            <div key={day}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{day}</p>
              <ul className="space-y-2">
                {dayEvents.map((event) => (
                  <li key={event.id}>
                    <EventLinkCard event={event} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const groupByDay = (events: Event[]): [string, Event[]][] => {
  const groups = new Map<string, Event[]>();
  events.forEach((e) => {
    const key = dayLabel(e.startTime);
    groups.set(key, [...(groups.get(key) ?? []), e]);
  });
  return Array.from(groups.entries());
};

const EventLinkCard: React.FC<{ event: Event }> = ({ event }) => {
  const summary = event.responsesEnabled ? event.responseSummary : undefined;
  const place = event.venue?.name || event.location;
  const group = eventGroupLabel(event);

  return (
    <Link
      to={`/events/${event.id}`}
      className={`group flex items-stretch gap-3 rounded-lg bg-white p-3 shadow-sm ring-1 ring-gray-200 transition hover:shadow-md hover:ring-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        event.isCancelled ? 'opacity-60' : ''
      }`}
    >
      <span className={`w-1 shrink-0 rounded-full ${typeAccent[event.type]}`} aria-hidden="true" />
      <div className="w-14 shrink-0 pt-0.5">
        <div className="text-sm font-semibold tabular-nums text-gray-900">{time(event.startTime)}</div>
        <div className="text-xs tabular-nums text-gray-400">{time(event.endTime)}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`truncate font-medium text-gray-900 ${event.isCancelled ? 'line-through' : ''}`}>{event.title}</span>
          {event.isCancelled && <span className="text-xs font-semibold text-red-600">Elmarad</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
          <span className={`rounded px-1.5 py-0.5 font-medium ${typeBadge[event.type]}`}>{getEventTypeLabel(event.type)}</span>
          {group && <span>{group}</span>}
          {place && (
            <span className="inline-flex min-w-0 items-center gap-0.5">
              <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{place}</span>
            </span>
          )}
        </div>
      </div>
      {summary && (
        <div className="flex shrink-0 flex-col items-end justify-center text-xs tabular-nums">
          <span className="font-semibold text-green-700">{summary.attendingCount} jön</span>
          {summary.notAttendingCount > 0 && <span className="text-red-600">{summary.notAttendingCount} nem</span>}
          {summary.noResponseCount > 0 && <span className="text-gray-400">{summary.noResponseCount} ?</span>}
        </div>
      )}
      <ChevronRightIcon className="h-5 w-5 shrink-0 self-center text-gray-300 group-hover:text-primary-500" />
    </Link>
  );
};

export default DashboardEvents;
